import { Component, computed, inject } from "@angular/core";
import { PopupService } from "../popup.service";
import { CompareObjectsService } from "../../compare-objects/compare-objects.service";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { ConversionService } from "@services/conversion.service";
import { DecimalPipe } from "@angular/common";
import { OptionService } from "../../option/option.service";
import { PortfolioLocationObject } from "../../../shared/contract/Portfolio/PortfolioLocation";
import { CompareObject } from "../../../shared/contract/AVM/CompareObject/CompareObject";
import { InfoTextService } from "@services/info-text.service";
import { MapFeatureInfoService } from "@services/map-feature-info.service";

import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { ResidentialPopupInfoComponent } from "../residential-popup-info/residential-popup-info.component";
import { PortfolioPopupInfoComponent } from "../portfolio-popup-info/portfolio-popup-info.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { PopupMode } from "@enums/popup-mode.enum";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";

@Component({
  selector: "app-popup",
  templateUrl: "./popup.component.html",
  styleUrls: ["./popup.component.scss"],
  imports: [
    FormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    ResidentialPopupInfoComponent,
    PortfolioPopupInfoComponent,
    TranslocoPipe
  ]
})
export class PopupComponent {
  popupService = inject(PopupService);
  mapFeatureInfoService = inject(MapFeatureInfoService);
  conversionService = inject(ConversionService);
  private readonly compareObjectService = inject(CompareObjectsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly optionService = inject(OptionService);
  private readonly infoTextService = inject(InfoTextService);
  private readonly thematicInfoService = inject(ThematicInfoService);

  readonly PopupMode = PopupMode;
  private readonly ObjectInfoText = [
    { Name: "ConstructionYear", Property: "ConstructionYear" },
    { Name: "LivingSpace", Property: "LivingSpace" },
    { Name: "ObjectType", Property: "ObjectSubType" },
    { Name: "PlotSize", Property: "PlotSize" },
    { Name: "PlotType", Property: "PlotType" }
  ];

  protected titleText = computed(() => {
    const mode = this.popupService.mode();
    const isLayerPopup = this.popupService.isLayerPopup();

    if (isLayerPopup) {
      const activeLayer = this.popupService.activeLayer();
      const infoText = this.infoTextService.getInfoTextByLocale(activeLayer?.DisplayName);
      return infoText?.[0]?.Text ?? "";
    }

    const selectedObjectDetails = this.popupService.selectedPortfolioDetails();
    const selectedPortfolioObject = this.popupService.selectedPortfolioObject();
    if (!mode) {
      return "";
    }

    switch (mode) {
      case PopupMode.Portfolio: {
        if (!selectedPortfolioObject && !selectedObjectDetails) {
          return "";
        }
        const address = selectedPortfolioObject ? selectedPortfolioObject.Address : selectedObjectDetails.Address;
        return address.Street + " " + address.HouseNumber;
      }
      case PopupMode.ResidentialRealEstate:
      case PopupMode.CommercialRealEstate:
        return this.translocoService.translate("Title.MainTitle.CompareObject");
    }
  });

  protected buttonText = computed(() => {
    const mode = this.popupService.mode();
    switch (mode) {
      case PopupMode.Portfolio:
        return "Button.OpenPortfolioObject";
      case PopupMode.ResidentialRealEstate:
      case PopupMode.CommercialRealEstate:
        return "Button.Adaptation";
    }
  });

  protected priceTitle = computed(() => {
    const selectedObject = this.popupService.selectedCompareObject();
    const isRent = this.thematicInfoService.isRent();
    if (!selectedObject) {
      return "-";
    }

    if (isRent) {
      return "Properties.CompareObjectType.Rent." + selectedObject.CompareType;
    }

    return "Properties.CompareObjectType.Purchase." + selectedObject.CompareType;
  });

  protected priceValue = computed(() => {
    const selectedObject = this.popupService.selectedCompareObject();
    if (!selectedObject) {
      return null;
    }
    return selectedObject.Prices.Original.Price;
  });

  protected setPropertyText(property: string): string {
    if (this.ObjectInfoText.find(e => e.Property === property) !== undefined) {
      return this.translocoService.translate("Module.ObjectInformation." + this.ObjectInfoText.find(e => e.Property === property)?.Name);
    } else if (property === "Purchase" || property === "Rent") {
      return this.translocoService.translate(
        "Properties.CompareObjectType." + property + "." + this.popupService.selectedCompareObject().CompareType
      );
    } else if (property === "TransactionYear") {
      return this.translocoService.translate("Table.Header.SalesYear");
    } else if (property === "CommercialType") {
      return this.translocoService.translate("Module.ObjectInformation.ObjectType");
    }
  }

  protected setProperty(property: string): string {
    const selectedObject = this.popupService.selectedCompareObject();

    if (!selectedObject) {
      return "";
    }

    if (property === "ObjectSubType") {
      return (
        this.translocoService.translate(
          "Properties.ObjectType.LongVersion." +
            this.conversionService.convertObjectSubTypeToObjectType(
              selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"]
                .ObjectSubType
            )
        ) +
        " (" +
        this.translocoService.translate(
          "Properties.ObjectSubType.LongVersion." +
            selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"].ObjectSubType
        ) +
        ")"
      );
    } else if (property === "CommercialType") {
      return this.translocoService.translate(
        "Properties.ObjectSubType.LongVersion." +
          selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"].ObjectSubType
      );
    } else if (property === "LivingSpace" || property === "PlotSize") {
      return (
        selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"][property] + " m²"
      );
    } else if (property === "PlotType") {
      return this.translocoService.translate("Properties.PlotType.LongVersion." + selectedObject.ObjectInformation.PlotType);
    } else if (property === "Purchase" || property === "Rent") {
      return (
        this.decimalPipe.transform(selectedObject.Prices.Original.Price, "1.0-0", this.optionService.locale()) +
        " " +
        this.popupService.unit[0]
      );
    } else {
      return selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"][property];
    }
  }

  protected selectPortfolioObject(locationObject: PortfolioLocationObject) {
    this.popupService.selectedPortfolioDetails();
    this.popupService.setPortfolioObjectInfo(locationObject);
  }

  protected selectCompareObject(compareObject: CompareObject) {
    this.popupService.selectedCompareObjects.set([compareObject]);

    this.popupService.allowPopupNavigation.set(true);
    this.compareObjectService.selectedCompareObject.set(this.popupService.selectedCompareObject());
  }

  protected removeObject() {
    this.popupService.closeActivePopup();
  }

  protected navigate() {
    if (this.popupService.mode() === PopupMode.Portfolio) {
      this.popupService.goToPortfolioObjectClick.next(this.popupService.selectedPortfolioObject());
    } else if (
      this.popupService.mode() === PopupMode.ResidentialRealEstate ||
      this.popupService.mode() === PopupMode.CommercialRealEstate
    ) {
      this.popupService.goToAdaptionClick.next(this.popupService.selectedCompareObject());
    }
    this.popupService.closeActivePopup();
  }

  protected getLegendForMapFeature(color: boolean, mapFeatureValue: string): string {
    const convertedMapFeatureValue = this.conversionService.convertStringToNumber(mapFeatureValue);
    const unit =
      this.mapFeatureInfoService.mapFeatureInfo.Legend[0].Color === null
        ? " " + this.mapFeatureInfoService.mapFeatureInfo.Legend[0].DisplayText
        : "";
    let value = null;
    let selectedColor = "";

    // Get value and color from legend by selected map feature
    this.mapFeatureInfoService.mapFeatureInfo.Legend?.forEach((legendItem, index) => {
      if (legendItem.DisplayText?.includes("-")) {
        // If legend is a range (10 - 20), check if selected value is within the bounds
        if (
          convertedMapFeatureValue >= legendItem.DisplayText.split("-").map(e => this.conversionService.convertStringToNumber(e))[0] &&
          convertedMapFeatureValue <= legendItem.DisplayText.split("-").map(e => this.conversionService.convertStringToNumber(e))[1]
        ) {
          value = mapFeatureValue;
          selectedColor = legendItem.Color;
        }
      } else {
        // If legend is the last object, check if selected value is equal or greater than the bound
        if (
          index == this.mapFeatureInfoService.mapFeatureInfo.Legend.length - 1 &&
          convertedMapFeatureValue >= +legendItem.DisplayText?.match(/\d/g)
        ) {
          if (mapFeatureValue >= legendItem.DisplayText?.match(/\d/g)?.join("")) {
            value = mapFeatureValue;
            selectedColor = legendItem.Color;
          }
        } else if (index in [0, 1] && legendItem.DisplayText?.match(/\d/g)) {
          // If legend is the first object, check if selected value is equal or less than the bound
          if (convertedMapFeatureValue <= +legendItem.DisplayText?.match(/\d/g)?.join("")) {
            value = mapFeatureValue;
            selectedColor = legendItem.Color;
          }
        }
      }
    });

    // If there is no numeric range value, show text
    if (!value) {
      value =
        this.mapFeatureInfoService.mapFeatureInfo.Legend.find(e => e.Value === mapFeatureValue)?.DisplayText !== null &&
        this.mapFeatureInfoService.mapFeatureInfo.Legend.find(e => e.Value === mapFeatureValue)?.DisplayText !== undefined
          ? this.mapFeatureInfoService.mapFeatureInfo.Legend.find(e => e.Value === mapFeatureValue)?.DisplayText
          : mapFeatureValue;
      selectedColor = this.mapFeatureInfoService.mapFeatureInfo.Legend.find(e => e.Value === mapFeatureValue)?.Color;
    }

    return color ? selectedColor : value + unit;
  }
}
